// PDF generators using jsPDF + autotable
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const today = () => {
  const d = new Date();
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const nowStr = () =>
  new Date().toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function stockStatus(qty, threshold) {
  if (qty <= 0) return "ESAURITO";
  if (qty <= threshold) return "IN ESAURIMENTO";
  return "OK";
}

function header(doc, title, subtitle) {
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("RETI MOLLUSCHI", 14, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Gestione Magazzino - Depurazione Molluschi", 14, 16);

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, 14, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(subtitle || `Stampato il ${nowStr()}`, 14, 40);
}

function footer(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      doc.internal.pageSize.getWidth() - 30,
      doc.internal.pageSize.getHeight() - 8,
    );
    doc.text(
      `Reti Molluschi - ${today()}`,
      14,
      doc.internal.pageSize.getHeight() - 8,
    );
  }
}

export function generateGiacenzaPDF(products) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, "Giacenza Magazzino", `Stampato il ${nowStr()}`);

  const body = products.map((p) => {
    const status = stockStatus(p.quantity, p.min_threshold);
    return [
      p.code,
      p.name,
      `${p.quantity} ${p.unit}`,
      `${p.min_threshold} ${p.unit}`,
      status,
    ];
  });

  autoTable(doc, {
    startY: 48,
    head: [["Codice", "Prodotto", "Giacenza", "Soglia Minima", "Stato"]],
    body,
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: {
      fillColor: [30, 58, 138],
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 28, font: "courier" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "center", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 4) {
        const v = data.cell.raw;
        if (v === "ESAURITO") {
          data.cell.styles.textColor = [153, 27, 27];
          data.cell.styles.fillColor = [254, 226, 226];
        } else if (v === "IN ESAURIMENTO") {
          data.cell.styles.textColor = [113, 63, 18];
          data.cell.styles.fillColor = [254, 243, 199];
        } else {
          data.cell.styles.textColor = [3, 84, 63];
          data.cell.styles.fillColor = [222, 247, 236];
        }
      }
    },
  });

  // Summary
  const finalY = doc.lastAutoTable?.finalY || 48;
  const total = products.length;
  const esa = products.filter((p) => p.quantity <= 0).length;
  const ies = products.filter(
    (p) => p.quantity > 0 && p.quantity <= p.min_threshold,
  ).length;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text("RIEPILOGO", 14, finalY + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`Totale prodotti: ${total}`, 14, finalY + 19);
  doc.text(`In esaurimento: ${ies}`, 14, finalY + 25);
  doc.text(`Esauriti: ${esa}`, 14, finalY + 31);

  footer(doc);
  doc.save(`giacenza_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateRiordinoPDF(products) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(
    doc,
    "Lista Riordino",
    "Prodotti in esaurimento o esauriti - da riordinare",
  );

  const toReorder = products.filter((p) => p.quantity <= p.min_threshold);
  const body = toReorder.map((p) => {
    const mancante = Math.max(0, p.min_threshold - p.quantity);
    const suggerito = Math.max(
      Math.ceil(p.min_threshold * 2),
      Math.ceil(mancante + p.min_threshold),
    );
    const status = stockStatus(p.quantity, p.min_threshold);
    return [
      p.code,
      p.name,
      `${p.quantity} ${p.unit}`,
      `${p.min_threshold} ${p.unit}`,
      `${suggerito} ${p.unit}`,
      status,
      "",
    ];
  });

  if (body.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    doc.text(
      "Nessun prodotto da riordinare. Tutte le scorte sono sopra la soglia minima.",
      14,
      56,
    );
  } else {
    autoTable(doc, {
      startY: 48,
      head: [
        [
          "Codice",
          "Prodotto",
          "Giacenza",
          "Soglia",
          "Qtà Sugg.",
          "Stato",
          "Note Riordino",
        ],
      ],
      body,
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: {
        fillColor: [234, 88, 12],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 24, font: "courier" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right", fontStyle: "bold" },
        5: { halign: "center", fontStyle: "bold" },
        6: { cellWidth: 35 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 5) {
          const v = data.cell.raw;
          if (v === "ESAURITO") {
            data.cell.styles.textColor = [153, 27, 27];
            data.cell.styles.fillColor = [254, 226, 226];
          } else if (v === "IN ESAURIMENTO") {
            data.cell.styles.textColor = [113, 63, 18];
            data.cell.styles.fillColor = [254, 243, 199];
          }
        }
      },
    });

    const finalY = doc.lastAutoTable?.finalY || 48;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(
      `Totale articoli da riordinare: ${toReorder.length}`,
      14,
      finalY + 12,
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text("Firma responsabile: ____________________", 14, finalY + 26);
    doc.text("Data ordine: ____________________", 110, finalY + 26);
  }

  footer(doc);
  doc.save(`riordino_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateMovimentiPDF(movements, filterLabel = "") {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, "Storico Movimenti", filterLabel || `Stampato il ${nowStr()}`);

  const body = movements.map((m) => [
    new Date(m.timestamp).toLocaleString("it-IT"),
    m.product_code,
    m.product_name,
    m.type === "entrata" ? "Entrata" : "Uscita",
    `${m.type === "entrata" ? "+" : "-"}${m.quantity}`,
    `${m.balance_after}`,
    m.note || "",
  ]);

  autoTable(doc, {
    startY: 48,
    head: [
      ["Data/Ora", "Codice", "Prodotto", "Tipo", "Qtà", "Giacenza", "Note"],
    ],
    body,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: {
      fillColor: [30, 58, 138],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      1: { font: "courier" },
      3: { halign: "center" },
      4: { halign: "right", fontStyle: "bold" },
      5: { halign: "right" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 3) {
        if (data.cell.raw === "Entrata") {
          data.cell.styles.textColor = [3, 84, 63];
        } else {
          data.cell.styles.textColor = [153, 27, 27];
        }
      }
    },
  });

  footer(doc);
  doc.save(`movimenti_${new Date().toISOString().slice(0, 10)}.pdf`);
}
